import {
  faCircleExclamation,
} from '@fortawesome/pro-regular-svg-icons'
import { Flex, Text } from '@mantine/core'
import { useRouter } from 'next/router'
import { useContext } from 'react'

import { useAnalytics } from '@flecto/analytics'
import {
  useMutationCancelInsurance,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreInsurance } from '@flecto/types'
import { Modal, ModalsContext, TextDate, useNotifications } from '@flecto/ui'

export type ModalConfirmationCancelInsuranceProps = {
  insurance?: CoreInsurance
}

export function ModalConfirmationCancelInsurance({
  insurance,
}: ModalConfirmationCancelInsuranceProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const { closeModal } = useContext(ModalsContext)
  const { showNotification } = useNotifications()
  const router = useRouter()

  const track = useAnalytics('segment')

  const { mutateAsync: cancelInsurance, isLoading } =
    useMutationCancelInsurance()

  const handleCancel = async () => {
    try {
      await cancelInsurance({
        id: insurance?.internal_id ?? '',
        bookingId: insurance?.booking ? insurance.booking.internal_id : '',
      })

      track('INSURANCE_TABLE__CANCEL_CONFIRMATION', {
        source: router.asPath === '/insurances' ? 'table-page' : 'detail-page',
      })

      showNotification({
        type: 'success',
        message: t(
          'modal_confirmation_cancel_insurance.notification_cancel_success',
          {
            id: insurance?.internal_id ?? '',
          }
        ),
      })
    } catch {
      closeModal()
      showNotification({
        type: 'error',
        message: t(
          'modal_confirmation_cancel_insurance.notification_cancel_error',
          {
            id: insurance?.internal_id ?? '',
          }
        ),
      })
    }
  }

  return (
    <Modal size='md'>
      <Modal.Icon icon={faCircleExclamation} color='red' />
      <Modal.Title>
        {t('modal_confirmation_cancel_insurance.title')}
      </Modal.Title>
      <Modal.Content>
        <Flex gap='xs' wrap='wrap' c='dimmed'>
          <Text>
            {t('modal_confirmation_cancel_insurance.this_means')}{' '}
            <Text component='span' fw={500}>
              {insurance?.item?.title}
            </Text>{' '}
            {t('modal_confirmation_cancel_insurance.be_protected')}{' '}
            {t('create_insurance.modal_confirmation.success_description_from')}{' '}
            <TextDate
              date={insurance?.start_at ?? ''}
              variant='datetime-brief'
              display={'inline'}
              fw={500}
            />{' '}
            {t('create_insurance.modal_confirmation.success_description_to')}{' '}
            <TextDate
              date={insurance?.end_at ?? ''}
              variant='datetime-brief'
              fw={500}
              display={'inline'}
            />
            . {t('modal_confirmation_cancel_insurance.cancellation_note')}
          </Text>
        </Flex>
      </Modal.Content>

      <Modal.Cancel>
        {t('modal_confirmation_cancel_insurance.go_back_btn')}
      </Modal.Cancel>

      <Modal.Submit onClick={() => handleCancel()} bg='red' loading={isLoading}>
        {t('modal_confirmation_cancel_insurance.yes_cancel_btn')}
      </Modal.Submit>
    </Modal>
  )
}
